<template>
    <Dialog v-model:visible="visible" :header="`Assign Template: ${template.name}`" :modal="true"
        :style="{ width: '600px' }">
        <div v-if="saving" class="text-center py-4">Assigning template...</div>
        <div class="space-y-3" v-else>
            <!-- 1. Selection Context -->
            <div class="space-y-3">
                <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">Select Class</label>
                    <Select v-model="form.classroomId" :options="classrooms" optionLabel="name" optionValue="id"
                        placeholder="Select a class" class="w-full" />
                </div>

                <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">Select Term (Academic Calendar)</label>
                    <AcademicCalendarSelect v-model="form.academicCalendarId" placeholder="Select a term"
                        className="w-full" />
                </div>
            </div>

            <!-- 3. Historical Mapping (Conditional) -->
            <div v-if="hasHistoricalColumns" class="bg-purple-50 p-2.5 rounded-md border border-purple-200 mt-2">
                <h3 class="font-semibold text-purple-900 mb-2 flex items-center text-sm">
                    <i class="pi pi-history mr-2"></i> Historical Data Mapping
                </h3>
                <p class="text-xs text-purple-800 mb-3">
                    This template uses data from previous terms. Please specify where to find the data for each column.
                </p>

                <div class="space-y-3">
                    <div v-for="col in historicalColumns" :key="col.key" class="grid grid-cols-1 gap-1 items-start">
                        <div class="text-sm font-medium flex justify-between">
                            <span>{{ col.label }}</span>
                            <span class="text-xs text-gray-500">Source: {{ col.calculation_arguments }}</span>
                        </div>
                        <Select v-model="form.historicalMapping[col.key]" :options="academicCalendars"
                            optionLabel="label" optionValue="id" :placeholder="`Select Source ${labels.TERM}`"
                            class="w-full p-inputtext-sm" />
                    </div>
                </div>
            </div>
        </div>

        <template #footer>
            <div class="flex justify-end space-x-3 pt-4">
                <Button size="small" label="Cancel" icon="pi pi-times" text @click="closeModal" severity="secondary" data-testid="btn-assign-template-cancel" />
                <Button size="small" label="Assign Template" icon="pi pi-check" @click="saveAssignment" :loading="saving"
                    :disabled="!isValid" data-testid="btn-assign-template-confirm" />
            </div>
        </template>
    </Dialog>
</template>

<script setup lang="ts">
import { useToast } from 'vue-toastification';
import { useAssignResultTemplateToClassroomMutation } from '@/types/vue-apollo-helpers';
import { useSchoolQuery } from '@/composables/queries/useSchoolQuery';
import { useSchoolLabels } from '@/composables/useSchoolLabels';

const { labels } = useSchoolLabels();
import type { ResultTemplate, AcademicCalendar, ResultTemplateColumn } from '@/types/composition-functions';
// PrimeVue
import AcademicCalendarSelect from '@/components/common/AcademicCalendarSelect.vue';

const props = defineProps<{
    template: ResultTemplate;
}>();

const emit = defineEmits(['close', 'assigned']);

const toast = useToast();
const visible = ref(true);
const saving = ref(false);

const form = reactive({
  classroomId: '',
  academicCalendarId: '',
  historicalMapping: {} as Record<string, string>,
});

// Watch visible to emit close
watch(visible, (val) => {
  if (!val) emit('close');
});

function closeModal() {
  visible.value = false;
}

// Queries — shared, cache-first school data (see useSchoolQuery)
const { classrooms, academicCalendars: schoolAcademicCalendars } = useSchoolQuery();

// Computed
const academicCalendars = computed(() => 
  // Format calendars for better display
  (schoolAcademicCalendars.value || [])
    .filter((c): c is AcademicCalendar => !!c)
    .map((c) => ({
      id: c.id,
      label: `${c.session?.name || 'Session'} - ${c.termStructure?.name || 'Term'}`,
    })),
);

const historicalColumns = computed(() => {
  if (!props.template) return [];
  return props.template.columns.filter((c: ResultTemplateColumn) => c.type === 'HISTORICAL');
});

const hasHistoricalColumns = computed(() => historicalColumns.value.length > 0);

const isValid = computed(() => {
  if (!form.classroomId || !form.academicCalendarId) return false;
  if (hasHistoricalColumns.value) {
    return historicalColumns.value.every((c: ResultTemplateColumn) => !!form.historicalMapping[c.key]);
  }
  return true;
});

const { mutate: assignTemplate, onDone, onError } = useAssignResultTemplateToClassroomMutation();

onDone(() => {
  saving.value = false;
  toast.success('Template assigned successfully');
  emit('assigned');
  closeModal();
});

onError((error) => {
  saving.value = false;
  toast.error('Failed to assign template. Please try again.');
});

async function saveAssignment() {
  if (!isValid.value) {
    toast.error('Please fill all required fields');
    return;
  }

  saving.value = true;

  const historicalSourceMap = historicalColumns.value.map((col: ResultTemplateColumn) => ({
    key: col.key,
    academic_calendar_id: form.historicalMapping[col.key],
  }));

  try {
    await assignTemplate({
      classroom_id: form.classroomId,
      academic_calendar_id: form.academicCalendarId,
      result_template_id: props.template.id,
      historical_source_map: historicalSourceMap,
    });
  } catch (e) {
    // Error handled by onError
  }
}
</script>
